import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "urlInput", "urlButton", "textInput", "textButton",
    "loading", "error", "errorMessage",
    "name", "description", "servings", "prepTime", "cookTime", "difficulty",
    "sourceName", "sourceUrl", "imageUrl", "tip",
    "ingredientsContainer", "stepsContainer",
    "newIngredientModal", "newIngredientName", "newIngredientCategory",
    "newIngredientUnit", "newIngredientError"
  ]

  static values = {
    parseUrl: String,
    parseText: String
  }

  connect() {
    this.currentSelectTarget = null
  }

  async importFromUrl() {
    const url = this.urlInputTarget.value.trim()
    if (!url) return

    this.showLoading()
    this.urlButtonTarget.disabled = true

    try {
      const response = await this.fetchParse(this.parseUrlValue, { url })
      this.populateForm(response)
      this.showManualTab()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.hideLoading()
      this.urlButtonTarget.disabled = false
    }
  }

  async importFromText() {
    const text = this.textInputTarget.value.trim()
    if (!text) return

    this.showLoading()
    this.textButtonTarget.disabled = true

    try {
      const response = await this.fetchParse(this.parseTextValue, { text })
      this.populateForm(response)
      this.showManualTab()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.hideLoading()
      this.textButtonTarget.disabled = false
    }
  }

  async fetchParse(endpoint, params) {
    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(params)
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || "Error al procesar la receta")
    }
    return data
  }

  populateForm(data) {
    if (data.name && this.hasNameTarget) this.nameTarget.value = data.name
    if (data.description && this.hasDescriptionTarget) this.descriptionTarget.value = data.description
    if (data.servings && this.hasServingsTarget) this.servingsTarget.value = data.servings
    if (data.prep_time_minutes && this.hasPrepTimeTarget) this.prepTimeTarget.value = data.prep_time_minutes
    if (data.cook_time_minutes && this.hasCookTimeTarget) this.cookTimeTarget.value = data.cook_time_minutes
    if (data.difficulty && this.hasDifficultyTarget) this.difficultyTarget.value = data.difficulty
    if (data.source_name && this.hasSourceNameTarget) this.sourceNameTarget.value = data.source_name
    if (data.source_url && this.hasSourceUrlTarget) this.sourceUrlTarget.value = data.source_url
    if (data.image_url && this.hasImageUrlTarget) this.imageUrlTarget.value = data.image_url
    if (data.tip && this.hasTipTarget) this.tipTarget.value = data.tip

    if (data.ingredients && this.hasIngredientsContainerTarget) {
      this.populateIngredients(data.ingredients)
    }

    if (data.steps && this.hasStepsContainerTarget) {
      this.populateSteps(data.steps)
    }
  }

  populateIngredients(ingredients) {
    this.ingredientsContainerTarget.innerHTML = ""
    const ingredientsData = JSON.parse(document.getElementById("ingredients-data").textContent)
    const claimedIds = new Set()

    ingredients.forEach((ing, index) => {
      const isNew = ing.is_new === true
      let matchedIngredient = isNew ? null : this.findMatchingIngredient(ing.name, ingredientsData)
      if (matchedIngredient && claimedIds.has(matchedIngredient.id)) {
        matchedIngredient = null
      }
      if (matchedIngredient) claimedIds.add(matchedIngredient.id)
      const row = this.createIngredientRow(ing, matchedIngredient, index, isNew)
      this.ingredientsContainerTarget.insertAdjacentHTML("beforeend", row)
    })
  }

  findMatchingIngredient(name, ingredientsData) {
    const normalized = this.normalize(name.toLowerCase())
    return ingredientsData.find(i =>
      this.normalize(i.name.toLowerCase()) === normalized ||
      this.normalize(i.name.toLowerCase()).includes(normalized) ||
      normalized.includes(this.normalize(i.name.toLowerCase()))
    )
  }

  normalize(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  }

  createIngredientRow(ing, matched, index, isNew = false) {
    const ingredientsData = JSON.parse(document.getElementById("ingredients-data").textContent)
    const timestamp = Date.now() + index

    let optionsHtml = '<option value="">Seleccionar ingrediente...</option>'
    const grouped = {}
    ingredientsData.forEach(i => {
      if (!grouped[i.category]) grouped[i.category] = []
      grouped[i.category].push(i)
    })

    for (const [category, items] of Object.entries(grouped)) {
      optionsHtml += `<optgroup label="${category}">`
      items.forEach(item => {
        const selected = matched && matched.id === item.id ? "selected" : ""
        optionsHtml += `<option value="${item.id}" ${selected}>${item.name}</option>`
      })
      optionsHtml += "</optgroup>"
    }

    const needsNew = isNew || !matched
    const notesValue = needsNew
      ? `[${ing.name}] ${ing.notes || ""}`.trim()
      : (ing.notes || "")

    const selectBorder = needsNew ? "border-amber-500/50" : "border-cream/10"
    const addBtnClass = needsNew
      ? "px-2 py-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 transition-colors cursor-pointer"
      : "px-2 py-2 bg-charcoal border border-cream/10 text-cream/40 hover:text-terracotta hover:border-terracotta/50 transition-colors cursor-pointer"

    const suggestedCategory = ing.suggested_category || ""
    const defaultUnit = ing.default_unit || ""

    return `
      <div class="ingredient-row flex gap-2 items-start" data-new-record="true" data-suggested-name="${ing.name}" data-suggested-category="${suggestedCategory}" data-suggested-unit="${defaultUnit}">
        <input type="number"
               name="recipe[recipe_ingredients_attributes][${timestamp}][quantity]"
               step="0.01" min="0"
               value="${ing.quantity || ""}"
               placeholder="Cant"
               class="w-20 bg-charcoal border border-cream/10 px-3 py-2 text-cream text-sm focus:border-terracotta focus:outline-none transition-colors">
        <input type="text"
               name="recipe[recipe_ingredients_attributes][${timestamp}][unit]"
               value="${ing.unit || ""}"
               placeholder="Unidad"
               class="w-24 bg-charcoal border border-cream/10 px-3 py-2 text-cream text-sm focus:border-terracotta focus:outline-none transition-colors">
        <div class="flex-1 flex gap-1">
          <select name="recipe[recipe_ingredients_attributes][${timestamp}][ingredient_id]"
                  class="flex-1 bg-charcoal border ${selectBorder} px-3 py-2 text-cream text-sm focus:border-terracotta focus:outline-none transition-colors appearance-none cursor-pointer">
            ${optionsHtml}
          </select>
          <button type="button"
                  data-action="click->recipe-import#openNewIngredientModal"
                  class="${addBtnClass}"
                  title="${needsNew ? 'Crear ingrediente nuevo' : 'Crear nuevo ingrediente'}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
          </button>
        </div>
        <input type="text"
               name="recipe[recipe_ingredients_attributes][${timestamp}][notes]"
               value="${notesValue}"
               placeholder="Notas"
               class="w-40 bg-charcoal border border-cream/10 px-3 py-2 text-cream text-sm focus:border-terracotta focus:outline-none transition-colors">
        <input type="hidden" name="recipe[recipe_ingredients_attributes][${timestamp}][position]" value="${index}">
        <button type="button" data-action="click->recipe-form#removeRow" class="p-2 text-cream/30 hover:text-red-400 transition-colors cursor-pointer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `
  }

  populateSteps(steps) {
    this.stepsContainerTarget.innerHTML = ""

    steps.forEach((step, index) => {
      const timestamp = Date.now() + index
      const instruction = typeof step === "string" ? step : step.instruction

      const row = `
        <div class="step-row flex gap-3 items-start" data-new-record="true">
          <div class="w-8 h-8 bg-charcoal text-cream/50 flex items-center justify-center font-mono text-sm flex-shrink-0 step-number">
            ${index + 1}
          </div>
          <textarea name="recipe[recipe_steps_attributes][${timestamp}][instruction]"
                    rows="2"
                    placeholder="Describe este paso..."
                    data-action="input->recipe-import#autoResize"
                    class="flex-1 bg-charcoal border border-cream/10 px-3 py-2 text-cream text-sm focus:border-terracotta focus:outline-none transition-colors resize-y scrollbar-hidden">${instruction}</textarea>
          <input type="hidden" name="recipe[recipe_steps_attributes][${timestamp}][position]" value="${index}">
          <button type="button" data-action="click->recipe-form#removeRow" class="p-2 text-cream/30 hover:text-red-400 transition-colors cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `
      this.stepsContainerTarget.insertAdjacentHTML("beforeend", row)
    })

    setTimeout(() => {
      this.stepsContainerTarget.querySelectorAll("textarea").forEach(ta => {
        ta.style.height = "auto"
        ta.style.height = ta.scrollHeight + "px"
      })
    }, 10)
  }

  autoResize(event) {
    const textarea = event.target
    textarea.style.height = "auto"
    textarea.style.height = textarea.scrollHeight + "px"
  }

  showManualTab() {
    const manualTabButton = this.element.querySelector("[data-tab='manual']")
    if (manualTabButton) manualTabButton.click()
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.hideError()
  }

  hideLoading() {
    this.loadingTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.classList.add("hidden")
  }

  openNewIngredientModal(event) {
    const row = event.currentTarget.closest(".ingredient-row")
    this.currentSelectTarget = row.querySelector("select")

    const categoriesData = JSON.parse(document.getElementById("categories-data").textContent)
    const categorySelect = this.newIngredientCategoryTarget
    categorySelect.innerHTML = '<option value="">Seleccionar...</option>'
    categoriesData.forEach(cat => {
      categorySelect.innerHTML += `<option value="${cat.id}">${cat.name}</option>`
    })

    const suggestedName = row.dataset.suggestedName || ""
    const suggestedCategory = row.dataset.suggestedCategory || ""
    const suggestedUnit = row.dataset.suggestedUnit || ""

    if (suggestedName) {
      this.newIngredientNameTarget.value = suggestedName
    } else {
      const notesInput = row.querySelector("input[name*='[notes]']")
      const notesValue = notesInput?.value || ""
      const bracketMatch = notesValue.match(/^\[([^\]]+)\]/)
      this.newIngredientNameTarget.value = bracketMatch ? bracketMatch[1] : ""
    }

    if (suggestedCategory) {
      const matchingCat = categoriesData.find(c =>
        c.name.toLowerCase() === suggestedCategory.toLowerCase()
      )
      if (matchingCat) {
        categorySelect.value = matchingCat.id
      }
    }

    this.newIngredientUnitTarget.value = suggestedUnit
    this.newIngredientErrorTarget.classList.add("hidden")
    this.newIngredientModalTarget.classList.remove("hidden")
  }

  closeNewIngredientModal() {
    this.newIngredientModalTarget.classList.add("hidden")
    this.currentSelectTarget = null
  }

  async createNewIngredient() {
    const name = this.newIngredientNameTarget.value.trim()
    const categoryId = this.newIngredientCategoryTarget.value
    const defaultUnit = this.newIngredientUnitTarget.value.trim()

    if (!name || !categoryId || !defaultUnit) {
      this.newIngredientErrorTarget.textContent = "Todos los campos son obligatorios"
      this.newIngredientErrorTarget.classList.remove("hidden")
      return
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']").content

    try {
      const response = await fetch("/ingredients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ingredient: {
            name: name,
            ingredient_category_id: categoryId,
            default_unit: defaultUnit
          }
        })
      })

      const data = await response.json()

      if (!response.ok) {
        this.newIngredientErrorTarget.textContent = data.errors?.join(", ") || "Error al crear ingrediente"
        this.newIngredientErrorTarget.classList.remove("hidden")
        return
      }

      this.addIngredientToAllSelects(data)

      if (this.currentSelectTarget) {
        this.currentSelectTarget.value = data.id
        this.currentSelectTarget.classList.remove("border-amber-500/50")
        this.currentSelectTarget.classList.add("border-cream/10")

        const notesInput = this.currentSelectTarget.closest(".ingredient-row").querySelector("input[name*='[notes]']")
        if (notesInput) {
          notesInput.value = notesInput.value.replace(/^\[[^\]]+\]\s*/, "")
        }
      }

      this.closeNewIngredientModal()
    } catch (_error) {
      this.newIngredientErrorTarget.textContent = "Error de conexión"
      this.newIngredientErrorTarget.classList.remove("hidden")
    }
  }

  addIngredientToAllSelects(ingredient) {
    const ingredientsData = JSON.parse(document.getElementById("ingredients-data").textContent)
    ingredientsData.push(ingredient)
    document.getElementById("ingredients-data").textContent = JSON.stringify(ingredientsData)

    const selects = this.ingredientsContainerTarget.querySelectorAll("select")
    selects.forEach(select => {
      let optgroup = select.querySelector(`optgroup[label="${ingredient.category}"]`)
      if (!optgroup) {
        optgroup = document.createElement("optgroup")
        optgroup.label = ingredient.category
        select.appendChild(optgroup)
      }

      const option = document.createElement("option")
      option.value = ingredient.id
      option.textContent = ingredient.name
      optgroup.appendChild(option)
    })
  }
}
